import { resolveFieldLabel } from "@repo/shared";
import type { FinancialEntryItem, FinancialFieldDefinition } from "@repo/shared/types";
import { Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import type { IncomeStabilityType } from "@/lib/types/flow";
import { cn } from "@/lib/utils";
import { T } from "./income-tab.i18n";
import { RequiredTypeLabel } from "./required-type-label";
import { getFieldLabel } from "./translation-utils";
import { TypeSummary } from "./type-summary";
import { formatNumberInput, MAX_ITEMS_PER_CATEGORY, parseNumberInput } from "./utils";

type IncomeTabProps = {
  entries: FinancialEntryItem[];
  fieldDefs: FinancialFieldDefinition[];
  /** QL-39 — ids of the required fields; their label gets a * and their last row cannot be removed. */
  requiredTypeIds?: ReadonlySet<string>;
  /** QL-39 — required fields still lacking an amount; their amount input is outlined. */
  missingRequiredIds?: ReadonlySet<string>;
  disabled: boolean;
  currency: string;
  lang: string;
  incomeStability: IncomeStabilityType | undefined;
  showStabilityQuestion: boolean;
  totalAnnualIncome: number;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, updates: Partial<FinancialEntryItem>) => void;
  onStabilityChange: (value: IncomeStabilityType) => void;
};

export function IncomeTab({
  entries,
  fieldDefs,
  requiredTypeIds,
  missingRequiredIds,
  disabled,
  currency,
  lang,
  incomeStability,
  showStabilityQuestion,
  totalAnnualIncome,
  onAdd,
  onRemove,
  onUpdate,
  onStabilityChange,
}: IncomeTabProps) {
  const { formatNumber, parseNumber } = useRegionalFormat();
  const t = useT(T);
  const isRequired = (typeId: string) => requiredTypeIds?.has(typeId) === true;
  const isMissing = (typeId: string) => missingRequiredIds?.has(typeId) === true;
  const isLastRowOfRequired = (entry: FinancialEntryItem) =>
    isRequired(entry.typeId) && entries.filter((e) => e.typeId === entry.typeId).length === 1;
  const [incomeEntryMode, setIncomeEntryMode] = useState<"monthly" | "annual">("annual");

  const displayMultiplier = incomeEntryMode === "monthly" ? 1 / 12 : 1;
  const storageMultiplier = incomeEntryMode === "monthly" ? 12 : 1;

  // QL-586 — the model's current label, not the one stored when the row was created.
  const labelFor = (entry: FinancialEntryItem) => {
    const def = fieldDefs.find((d) => d.id === entry.typeId);
    return (def ? resolveFieldLabel(def, lang) : "") || entry.typeLabel;
  };

  const summaryByType = useMemo(() => {
    const grouped: Record<string, { label: string; total: number }> = {};
    for (const entry of entries) {
      if (!grouped[entry.typeId]) {
        grouped[entry.typeId] = { label: labelFor(entry), total: 0 };
      }
      grouped[entry.typeId].total += entry.amount || 0;
    }
    return grouped;
  }, [entries, fieldDefs, lang]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-base font-medium">{t.incomeAfterTax}</h4>
        <div className="flex items-center gap-1 rounded-lg border p-1">
          <Button
            type="button"
            variant={incomeEntryMode === "monthly" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setIncomeEntryMode("monthly")}
            className="h-7 px-3 text-xs"
          >
            {t.monthly}
          </Button>
          <Button
            type="button"
            variant={incomeEntryMode === "annual" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setIncomeEntryMode("annual")}
            className="h-7 px-3 text-xs"
          >
            {t.annual}
          </Button>
        </div>
      </div>

      {/* Entry list */}
      <div className="space-y-3">
        {entries.length > 0 && (
          <div className="grid gap-2 text-sm font-medium text-muted-foreground grid-cols-[minmax(100px,375px)_minmax(80px,165px)_minmax(130px,270px)_32px]">
            <div>{t.description}</div>
            <div>{t.amount}</div>
            <div>{t.type}</div>
            <div />
          </div>
        )}

        {entries.map((entry) => (
          <div
            key={entry.id}
            className="grid gap-2 items-center grid-cols-[minmax(100px,375px)_minmax(80px,165px)_minmax(130px,270px)_32px]"
          >
            <Input
              value={entry.label}
              onChange={(e) => onUpdate(entry.id, { label: e.target.value })}
              disabled={disabled}
              placeholder={t.descriptionPlaceholder}
              className="h-8"
            />

            <Input
              type="text"
              inputMode="numeric"
              value={formatNumberInput(
                entry.amount === undefined
                  ? undefined
                  : Math.round(entry.amount * displayMultiplier),
                formatNumber,
              )}
              onChange={(e) => {
                const parsed = parseNumberInput(e.target.value, parseNumber);
                onUpdate(entry.id, {
                  amount: parsed === undefined ? undefined : parsed * storageMultiplier,
                });
              }}
              disabled={disabled}
              placeholder={t.amountPlaceholder}
              aria-invalid={isMissing(entry.typeId) || undefined}
              className={cn("h-8 text-right", isMissing(entry.typeId) && "border-destructive")}
            />

            <Select
              value={entry.typeId}
              onValueChange={(v) => onUpdate(entry.id, { typeId: v })}
              disabled={disabled}
            >
              <SelectTrigger className="h-8">
                <SelectValue>
                  <RequiredTypeLabel
                    label={getFieldLabel(entry.typeId, lang, fieldDefs, t.fieldLabels)}
                    required={isRequired(entry.typeId)}
                  />
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {fieldDefs.map((field) => (
                  <SelectItem key={field.id} value={field.id}>
                    {getFieldLabel(field.id, lang, fieldDefs, t.fieldLabels)}
                    {isRequired(field.id) && <span className="text-destructive"> *</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              tooltip={t.remove}
              onClick={() => onRemove(entry.id)}
              disabled={disabled || isLastRowOfRequired(entry)}
              className="h-8 w-8"
            >
              <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
            </Button>
          </div>
        ))}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onAdd}
          disabled={disabled || entries.length >= MAX_ITEMS_PER_CATEGORY || fieldDefs.length === 0}
          className="w-full"
        >
          {t.addIncome}
        </Button>

        {entries.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">{t.noItemsYet}</p>
        )}
      </div>

      {/* Income stability question */}
      {showStabilityQuestion && (
        <div className="border-t pt-4 mt-4">
          <div className="space-y-2">
            <Label>{t.incomeStability}</Label>
            <Select
              value={incomeStability}
              onValueChange={(v) => onStabilityChange(v as IncomeStabilityType)}
              disabled={disabled}
            >
              <SelectTrigger>
                <SelectValue placeholder={t.selectStability} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="permanent">{t.stabilityPermanent}</SelectItem>
                <SelectItem value="temporary">{t.stabilityTemporary}</SelectItem>
                <SelectItem value="variable">{t.stabilityVariable}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <TypeSummary
        summary={summaryByType}
        total={totalAnnualIncome}
        currency={currency}
        lang={lang}
      />
    </div>
  );
}
